import {
  ORDER_CANCELLED,
  ORDER_INVOICE_PAID,
  ORDER_INVOICE_REFUNDED,
  ORDER_MANUAL_SYNC_REQUEST,
  OrderCancelledEvent,
  OrderInvoicePaidEvent,
  OrderInvoiceRefundedEvent,
  OrderManualSyncRequestEvent,
  OrderSyncTriggerEvent
} from '../../types/events';
import { SnapshotModel } from '..';
import { Collections } from '../../constants';
import { SerializedModel } from '../types';
import { OrderModel } from './orderModel';
import { safeGetModelFromRef } from '../getModel';
import { ResponseOrError, wrapError, wrapSuccess } from '../../types/responses';
import { safeAsync } from '../../safeWrappers';
import { filterAsync } from '../../helpers/array/async';
import { OrganizationModel } from './organizationModel';
import { IntegrationSettingsModel } from './integrationSettingsModel';
import { getErrorReporter } from '../../utils/errors';
import { ColumnService } from '../../services/directory';
import { IntegrationStartWithSignalInput } from '../../types/integrationWorkflow/input';

export type SerializedOrderSyncTriggerEventModel = SerializedModel<
  typeof Collections.events
>;

export class OrderSyncTriggerEventModel extends SnapshotModel<
  OrderSyncTriggerEvent,
  typeof Collections.events
> {
  get type() {
    return Collections.events;
  }

  async getOrder() {
    return safeGetModelFromRef(OrderModel, this.ctx, this.modelData.order);
  }

  async groupSyncableOrders(): Promise<
    ResponseOrError<IntegrationStartWithSignalInput[]>
  > {
    const orderId = this.modelData.order.id;
    const [orderError, order] = await this.getOrder();
    if (orderError) {
      getErrorReporter().logAndCaptureError(
        ColumnService.INTEGRATIONS,
        orderError,
        'Failed to get order',
        { orderId }
      );
      return wrapError(orderError);
    }

    const [newspaperOrdersError, newspaperOrders] = await safeAsync(() =>
      order.getNewspaperOrders()
    )();

    if (newspaperOrdersError) {
      getErrorReporter().logAndCaptureError(
        ColumnService.INTEGRATIONS,
        newspaperOrdersError,
        'Failed to get newspaper orders',
        { orderId }
      );
      return wrapError(newspaperOrdersError);
    }

    const mapping = newspaperOrders.reduce((acc, newspaperOrder) => {
      const publisherId = newspaperOrder.modelData.newspaper.id;

      if (
        this.isManualSyncRequestEvent() &&
        this.modelData.data.publisher.id !== publisherId
      ) {
        return acc;
      }

      if (!acc[publisherId]) {
        acc[publisherId] = [];
      }

      acc[publisherId].push(newspaperOrder.id);

      return acc;
    }, {} as { [publisherId: string]: string[] });

    return filterAsync(
      Object.entries(mapping),
      async ([publisherId, newspaperOrderIds]) => {
        const [publisherError, publisher] = await safeGetModelFromRef(
          OrganizationModel,
          this.ctx,
          this.ctx.organizationsRef().doc(publisherId)
        );

        if (publisherError) {
          getErrorReporter().logAndCaptureError(
            ColumnService.INTEGRATIONS,
            publisherError,
            'Failed to get publisher',
            { orderId, publisherId }
          );
          return wrapError(publisherError);
        }

        const integrationSettingsRef =
          publisher.modelData.orderExport?.[order.modelData.product];

        if (!integrationSettingsRef) {
          return wrapSuccess(null);
        }

        const [integrationSettingsError, integrationSettings] =
          await safeGetModelFromRef(
            IntegrationSettingsModel,
            this.ctx,
            integrationSettingsRef
          );

        if (integrationSettingsError) {
          getErrorReporter().logAndCaptureError(
            ColumnService.INTEGRATIONS,
            integrationSettingsError,
            'Failed to get integration settings',
            {
              integrationSettingsId: integrationSettingsRef.id,
              orderId,
              publisherId
            }
          );
          return wrapError(integrationSettingsError);
        }

        const { triggers } = integrationSettings.modelData;

        if (!triggers[this.modelData.type]) {
          return wrapSuccess(null);
        }

        return wrapSuccess({
          eventId: this.id,
          newspaperOrderIds,
          orderId,
          publisherId,
          format: integrationSettings.modelData.format
        });
      }
    );
  }

  isRefundEvent(): this is SnapshotModel<
    OrderInvoiceRefundedEvent,
    typeof Collections.events
  > {
    return this.modelData.type === ORDER_INVOICE_REFUNDED;
  }

  isPaymentEvent(): this is SnapshotModel<
    OrderInvoicePaidEvent,
    typeof Collections.events
  > {
    return this.modelData.type === ORDER_INVOICE_PAID;
  }

  isCancellationEvent(): this is SnapshotModel<
    OrderCancelledEvent,
    typeof Collections.events
  > {
    return this.modelData.type === ORDER_CANCELLED;
  }

  isManualSyncRequestEvent(): this is SnapshotModel<
    OrderManualSyncRequestEvent,
    typeof Collections.events
  > {
    return this.modelData.type === ORDER_MANUAL_SYNC_REQUEST;
  }
}
